import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import Draggable from 'react-draggable';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  onSnapshot, // Import onSnapshot for real-time updates
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../config/firebase'; // Assuming auth and db are exported from your firebase.js

// Constants for shake detection
const SHAKE_THRESHOLD = 15;
const SHAKE_TIME_THRESHOLD = 500;

const AnnotationBox = ({ iframeRef }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [annotationText, setAnnotationText] = useState('');
  const [userId, setUserId] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dimensions, setDimensions] = useState({ width: 384, height: 288 }); // Default w-96 (384px), h-72 (288px) for better starting point
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(''); // 'saving', 'saved', 'error'
  const textareaRef = useRef(null);
  const annotationRef = useRef(null); // Ref for the draggable div to get its dimensions

  // New state for caching annotations
  const [annotationCache, setAnnotationCache] = useState(new Map());

  // Use state to manage the current URL path, updated by message event
  const [currentUrlPath, setCurrentUrlPath] = useState(
    window.location.pathname
  );

  const previousAnnotationText = useRef('');
  const inflightText = useRef('');

  // Shake detection variables
  const lastShakeTimeRef = useRef(0);
  const lastAccelerationRef = useRef({ x: null, y: null, z: null });
  const shakeCountRef = useRef(0);
  const shakeTimeoutRef = useRef(null);
  const isMobileDeviceRef = useRef(false);

  // Ref for the visibility timeout
  const visibilityTimeoutRef = useRef(null);

  // Detect mobile device on mount
  useEffect(() => {
    isMobileDeviceRef.current = /Mobi|Android/i.test(navigator.userAgent);
  }, []);

  // Set up user ID
  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
      }
    });
    return () => unsubscribeAuth();
  }, []);

  // Effect for cache invalidation based on page/component visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        visibilityTimeoutRef.current = setTimeout(() => {
          setAnnotationCache(new Map());
          console.log('Page was hidden for over 30 seconds. Cache dropped.');
        }, 30000);
      } else {
        if (visibilityTimeoutRef.current) {
          clearTimeout(visibilityTimeoutRef.current);
        }
      }
    };

    if (!isOpen) {
      setAnnotationCache(new Map());
      console.log('Annotation box closed. Cache dropped.');
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
      }
    };
  }, [isOpen, setAnnotationCache]);

  // Effect for URL change detection
  useEffect(() => {
    const handlePageChangeMessage = (event) => {
      if (
        event.data &&
        event.data.includes &&
        event.data.includes('action') &&
        JSON.parse(event.data).action === 'pageChanged'
      ) {
        let message = JSON.parse(event.data);
        const { bookParent, language, bookName, pageNo } = message.data;
        let newUrl = `/books/${bookParent}/${language}/${bookName}/pdf/${pageNo}`;

        if (newUrl !== currentUrlPath) {
          console.log('Received PageChanged message. New URL:', newUrl);
          inflightText.current = '';
          setAnnotationText('');
          previousAnnotationText.current = '';
          setCurrentUrlPath(newUrl);
        }
      }
    };

    window.addEventListener('message', handlePageChangeMessage);

    return () => {
      window.removeEventListener('message', handlePageChangeMessage);
    };
  }, [currentUrlPath]);

  // --- START OF MODIFIED CODE FOR REAL-TIME UPDATES ---
  // This effect now uses onSnapshot to listen for real-time updates from Firestore.
  useEffect(() => {
    // Exit if we don't have the required data to set up the listener.
    if (!userId || !db || !isOpen || !currentUrlPath) {
      return;
    }

    // Load non-realtime data (position, dimensions) from localStorage.
    // This is done once when the component opens for a specific URL.
    const savedPosition = localStorage.getItem(
      `annotation_position_${encodeURIComponent(currentUrlPath)}`
    );
    if (savedPosition) {
      setPosition(JSON.parse(savedPosition));
    } else {
      setPosition({ x: window.innerWidth / 4, y: window.innerHeight / 4 });
    }

    const savedDimensions = localStorage.getItem(
      `annotation_dimensions_${encodeURIComponent(currentUrlPath)}`
    );
    if (savedDimensions) {
      setDimensions(JSON.parse(savedDimensions));
    }

    // Define the document reference for the listener.
    const annotationDocRef = doc(
      db,
      `artifacts/${userId}/annotations`,
      encodeURIComponent(currentUrlPath)
    );

    // Set up the real-time listener.
    const unsubscribe = onSnapshot(
      annotationDocRef,
      (docSnap) => {
        let fetchedText = '';
        if (docSnap.exists()) {
          fetchedText = docSnap.data().text || '';
          console.log(
            `Real-time update for ${currentUrlPath} received from Firestore.`
          );
        } else {
          console.log(
            `No annotation for ${currentUrlPath} exists, or it was deleted.`
          );
          return;
        }

        // Update the component's state with the new text.
        if (!inflightText.current.startsWith(fetchedText)) {
          setAnnotationText(fetchedText);
          // CRITICAL: Sync the "previous text" ref to prevent a save-loop.
          previousAnnotationText.current = fetchedText;
          // Update the cache with the fresh data.
          setAnnotationCache((prevCache) =>
            new Map(prevCache).set(currentUrlPath, { text: fetchedText })
          );
        }
      },
      (error) => {
        console.error('Error with Firestore real-time listener:', error);
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(''), 3000);
      }
    );

    // Cleanup: Return the unsubscribe function to detach the listener when
    // the component closes, the URL changes, or the user logs out.
    return () => {
      unsubscribe();
      console.log(`Unsubscribed from real-time updates for ${currentUrlPath}.`);
    };
  }, [userId, currentUrlPath, db, isOpen]); // Re-runs when these change.
  // --- END OF MODIFIED CODE FOR REAL-TIME UPDATES ---

  // Save annotation data to Firestore
  const saveAnnotation = useCallback(
    async (dataToSave) => {
      if (!userId || !db || !currentUrlPath) {
        console.error(
          'Cannot save annotation: User not authenticated, Firestore not ready, or URL missing.'
        );
        return;
      }

      if (dataToSave.text !== undefined) {
        // Only save if the text has actually changed.
        if (dataToSave.text === previousAnnotationText.current) {
          console.log('Text has not changed. Skipping save.');
          return;
        }
        inflightText.current = dataToSave.text;
        setIsSaving(true);
        setSaveStatus('saving');
      }

      const annotationDocRef = doc(
        db,
        `artifacts/${userId}/annotations`,
        encodeURIComponent(currentUrlPath)
      );

      try {
        await setDoc(
          annotationDocRef,
          {
            ...dataToSave,
            timestamp: serverTimestamp(),
            url: currentUrlPath,
          },
          { merge: true }
        );

        if (dataToSave.text !== undefined) {
          // The cache is now updated by the onSnapshot listener, but we can
          // update it here for optimistic UI feel.
          setAnnotationCache((prevCache) =>
            new Map(prevCache).set(currentUrlPath, { text: dataToSave.text })
          );
          previousAnnotationText.current = dataToSave.text;
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus(''), 2000);
        }

        if (dataToSave.position) {
          localStorage.setItem(
            `annotation_position_${encodeURIComponent(currentUrlPath)}`,
            JSON.stringify(dataToSave.position)
          );
        }
        if (dataToSave.dimensions) {
          localStorage.setItem(
            `annotation_dimensions_${encodeURIComponent(currentUrlPath)}`,
            JSON.stringify(dataToSave.dimensions)
          );
        }

        console.log('Annotation data saved successfully!');
      } catch (error) {
        console.error('Error saving annotation:', error);
        if (dataToSave.text !== undefined) {
          setSaveStatus('error');
          setTimeout(() => setSaveStatus(''), 3000);
        }
      } finally {
        if (dataToSave.text !== undefined) {
          setIsSaving(false);
        }
      }
    },
    [userId, db, currentUrlPath]
  );

  // --- REMOVED ---
  // The following useEffect block for debounced auto-save on text change has been removed.
  /*
  useEffect(() => {
    const handler = setTimeout(() => {
      if (isOpen && annotationText !== previousAnnotationText.current) {
        console.log('Text changed, saving...');
        saveAnnotation({ text: annotationText });
      }
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [annotationText, saveAnnotation, isOpen]);
  */
  // --- END OF REMOVED ---

  // Debounced auto-save for position
  useEffect(() => {
    const handler = setTimeout(() => {
      if (isOpen) {
        saveAnnotation({ position: position });
      }
    }, 2000);

    return () => {
      clearTimeout(handler);
    };
  }, [position, saveAnnotation, isOpen]);

  // Debounced auto-save for dimensions
  useEffect(() => {
    const handler = setTimeout(() => {
      if (isOpen) {
        saveAnnotation({ dimensions: dimensions });
      }
    }, 2000);

    return () => {
      clearTimeout(handler);
    };
  }, [dimensions, saveAnnotation, isOpen]);

  // Event Listener for keyboard (parent window AND same-origin iframe)
  useEffect(() => {
    const handleToggleKey = (event) => {
      if (
        (!isMobileDeviceRef.current &&
          (event.ctrlKey || event.metaKey) &&
          event.altKey &&
          event.key === 'a') ||
        event.key === 'ā'
      ) {
        event.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleToggleKey);

    const iframeElement = iframeRef.current;
    let iframeDocument = null;

    const checkIframeAndAttach = () => {
      if (iframeElement && iframeElement.contentWindow) {
        try {
          iframeDocument = iframeElement.contentWindow.document;
          iframeDocument.addEventListener('keydown', handleToggleKey);
        } catch (e) {
          console.warn(
            'Could not access iframe document for keydown listener:',
            e
          );
          iframeDocument = null;
        }
      }
    };

    checkIframeAndAttach();
    if (iframeElement) {
      iframeElement.addEventListener('load', checkIframeAndAttach);
    }

    return () => {
      window.removeEventListener('keydown', handleToggleKey);
      if (iframeDocument) {
        iframeDocument.removeEventListener('keydown', handleToggleKey);
      }
      if (iframeElement) {
        iframeElement.removeEventListener('load', checkIframeAndAttach);
      }
    };
  }, [isMobileDeviceRef.current]);

  // Device shake detection for mobile
  useEffect(() => {
    if (!isMobileDeviceRef.current) return;

    const handleDeviceMotion = (event) => {
      const acceleration = event.accelerationIncludingGravity;
      if (!acceleration || acceleration.x === null) return;

      if (lastAccelerationRef.current.x == null) {
        lastAccelerationRef.current = { ...acceleration };
        return;
      }

      const dx = Math.abs(acceleration.x - lastAccelerationRef.current.x);
      const dy = Math.abs(acceleration.y - lastAccelerationRef.current.y);
      const dz = Math.abs(acceleration.z - lastAccelerationRef.current.z);
      const totalAccelerationChange = dx + dy + dz;
      const currentTime = new Date().getTime();

      if (totalAccelerationChange > SHAKE_THRESHOLD) {
        if (currentTime - lastShakeTimeRef.current > SHAKE_TIME_THRESHOLD) {
          shakeCountRef.current += 1;
          lastShakeTimeRef.current = currentTime;

          if (shakeCountRef.current >= 2) {
            setIsOpen(false); //((prev) => !prev);
            shakeCountRef.current = 0;
            clearTimeout(shakeTimeoutRef.current);
          }

          clearTimeout(shakeTimeoutRef.current);
          shakeTimeoutRef.current = setTimeout(() => {
            shakeCountRef.current = 0;
          }, SHAKE_TIME_THRESHOLD * 2);
        }
      }
      lastAccelerationRef.current = { ...acceleration };
    };

    const requestMotionPermission = async () => {
      if (typeof DeviceMotionEvent.requestPermission === 'function') {
        try {
          const permissionState = await DeviceMotionEvent.requestPermission();
          if (permissionState === 'granted') {
            window.addEventListener('devicemotion', handleDeviceMotion);
          } else {
            console.warn('Device motion permission denied.');
          }
        } catch (error) {
          console.error('Error requesting device motion permission:', error);
        }
      } else {
        window.addEventListener('devicemotion', handleDeviceMotion);
      }
    };
    requestMotionPermission();

    return () => {
      window.removeEventListener('devicemotion', handleDeviceMotion);
      clearTimeout(shakeTimeoutRef.current);
    };
  }, []);

  const handleStop = (e, ui) => {
    const newPosition = { x: ui.x, y: ui.y };
    setPosition(newPosition);
  };

  const initialPosition = useMemo(() => {
    if (!currentUrlPath)
      return { x: window.innerWidth / 4, y: window.innerHeight / 4 };
    const savedPos = localStorage.getItem(
      `annotation_position_${encodeURIComponent(currentUrlPath)}`
    );
    return savedPos
      ? JSON.parse(savedPos)
      : { x: window.innerWidth / 4, y: window.innerHeight / 4 };
  }, [currentUrlPath]);

  const handleResize = useCallback(() => {
    if (annotationRef.current) {
      const { offsetWidth, offsetHeight } = annotationRef.current;
      if (
        (Math.abs(offsetWidth - dimensions.width) > 5 ||
          Math.abs(offsetHeight - dimensions.height) > 5) &&
        offsetWidth > 0 &&
        offsetHeight > 0
      ) {
        setDimensions({ width: offsetWidth, height: offsetHeight });
      }
    }
  }, [dimensions]);

  useEffect(() => {
    let resizeObserver;
    const currentAnnotationRef = annotationRef.current;
    if (currentAnnotationRef) {
      resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.target === currentAnnotationRef) {
            handleResize();
          }
        }
      });
      resizeObserver.observe(currentAnnotationRef);
    }
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [handleResize]);

  // --- ADDED ---
  // This function handles the key press event in the textarea.
  const handleKeyDown = (event) => {
    // Save on 'Enter' key press, but allow new lines with 'Shift+Enter'.
    if (event.key === 'Enter' && !event.shiftKey) {
      console.log('Enter pressed, saving annotation...');
      saveAnnotation({ text: annotationText });
    }
  };
  // --- END OF ADDED ---

  if (!isOpen) {
    return null;
  }

  return (
    <Draggable
      handle=".handle"
      defaultPosition={initialPosition}
      position={null}
      onStop={handleStop}
      nodeRef={annotationRef}
    >
      <div
        ref={annotationRef}
        className="fixed z-[10000] bg-blue-500 rounded-lg shadow-xl text-white flex flex-col ease-in-out"
        style={{
          minWidth: '280px',
          minHeight: '180px',
          resize: 'both',
          overflow: 'auto',
          width: `${dimensions.width}px`,
          height: `${dimensions.height}px`,
          willChange: 'transform, width, height',
        }}
      >
        <div className="handle p-3 bg-blue-700 flex justify-between items-center rounded-t-lg cursor-move">
          <h4 className="font-bold text-lg">Annotation for Current URL</h4>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 rounded-full hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
            aria-label="Close annotation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="p-4 flex-grow">
          <textarea
            ref={textareaRef}
            className="w-full h-full p-2 border border-blue-400 rounded-md bg-blue-100 text-gray-900 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
            // --- MODIFIED ---
            placeholder="Write your annotation... Press Enter to save."
            // --- END OF MODIFIED ---
            value={annotationText}
            onChange={(e) => setAnnotationText(e.target.value)}
            // --- ADDED ---
            onKeyDown={handleKeyDown} // This triggers the save on Enter press.
            // --- END OF ADDED ---
            maxLength={4096}
          />
        </div>
        <div className="p-3 bg-blue-700 rounded-b-lg text-xs text-blue-200 flex justify-between items-center">
          <span>
            {saveStatus === 'saving' && 'Saving...'}
            {saveStatus === 'saved' && 'Saved!'}
            {saveStatus === 'error' && (
              <span className="text-red-300">Save Error!</span>
            )}
            {!saveStatus && 'Annotation ready.'}
          </span>
        </div>
      </div>
    </Draggable>
  );
};

export default AnnotationBox;
